import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Table, Project, TableWebhook } from '../../types/domain';
import { apiClient } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { PageContainer } from '../layout/PageContainer';
import { Modal } from '../ui/Modal';
import { ConfirmModal } from '../modals/ConfirmModal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Checkbox } from '../ui/Checkbox';
import { HedgehogSpinner } from '../brand/HedgehogSpinner';
import {
  Webhook,
  Plus,
  Trash2,
  Edit2,
  Copy,
  Check,
  Eye,
  EyeOff,
  Play,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  Zap,
} from 'lucide-react';

interface WebhooksTabProps {
  table: Table;
  project: Project;
  canEdit: boolean;
  headerActionContainer?: HTMLElement | null;
}

export const WebhooksTab: React.FC<WebhooksTabProps> = ({
  table,
  project,
  canEdit,
  headerActionContainer,
}) => {
  const { showToast } = useToast();

  const [webhooks, setWebhooks] = useState<TableWebhook[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<TableWebhook | null>(null);
  const [webhookToDelete, setWebhookToDelete] = useState<TableWebhook | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form states
  const [formName, setFormName] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const [formSecret, setFormSecret] = useState('');
  const [formEventTrigger, setFormEventTrigger] = useState('non_spam_only');
  const [formIsActive, setFormIsActive] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Secret visibility & copy state
  const [revealedSecrets, setRevealedSecrets] = useState<Record<string, boolean>>({});
  const [copiedSecrets, setCopiedSecrets] = useState<Record<string, boolean>>({});
  const [copiedUrls, setCopiedUrls] = useState<Record<string, boolean>>({});

  // Test ping state per webhook ID
  const [testingWebhookId, setTestingWebhookId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<
    Record<string, { success: boolean; message: string; duration_ms?: number }>
  >({});

  // Generate 64-character cryptographically secure hex secret
  const generateHmacSecret = () => {
    const arr = new Uint8Array(32);
    window.crypto.getRandomValues(arr);
    const hex = Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
    setFormSecret(hex);
  };

  const fetchWebhooks = useCallback(async () => {
    try {
      const res = await apiClient<{ data: TableWebhook[] }>(
        `/projects/${project.id}/tables/${table.id}/webhooks`
      );
      setWebhooks(res.data || []);
    } catch (err) {
      console.warn('Could not load webhooks:', err);
      setWebhooks([]);
    } finally {
      setIsLoading(false);
    }
  }, [project.id, table.id]);

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  const openCreateModal = () => {
    setEditingWebhook(null);
    setFormName('');
    setFormUrl('');
    generateHmacSecret();
    setFormEventTrigger('non_spam_only');
    setFormIsActive(true);
    setIsModalOpen(true);
  };

  const openEditModal = (hook: TableWebhook) => {
    setEditingWebhook(hook);
    setFormName(hook.name);
    setFormUrl(hook.url);
    setFormSecret(hook.secret);
    setFormEventTrigger(hook.event_trigger || 'non_spam_only');
    setFormIsActive(hook.is_active);
    setIsModalOpen(true);
  };

  const handleSaveWebhook = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim() || !formUrl.trim()) {
      showToast({ type: 'warning', title: 'Validation Error', message: 'Name and URL are required.' });
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: formName.trim(),
        url: formUrl.trim(),
        secret: formSecret.trim(),
        event_trigger: formEventTrigger,
        is_active: formIsActive,
      };

      if (editingWebhook) {
        const res = await apiClient<{ webhook: TableWebhook }>(
          `/projects/${project.id}/tables/${table.id}/webhooks/${editingWebhook.id}`,
          {
            method: 'PUT',
            body: JSON.stringify(payload),
          }
        );
        setWebhooks((prev) => prev.map((h) => (h.id === res.webhook.id ? res.webhook : h)));
        showToast({ type: 'success', title: 'Webhook Updated', message: `Webhook '${res.webhook.name}' saved.` });
      } else {
        const res = await apiClient<{ webhook: TableWebhook }>(
          `/projects/${project.id}/tables/${table.id}/webhooks`,
          {
            method: 'POST',
            body: JSON.stringify(payload),
          }
        );
        setWebhooks((prev) => [...prev, res.webhook]);
        showToast({ type: 'success', title: 'Webhook Created', message: `Webhook '${res.webhook.name}' created.` });
      }

      setIsModalOpen(false);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to Save Webhook',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteWebhook = async () => {
    if (!webhookToDelete) return;
    setIsDeleting(true);
    try {
      await apiClient(
        `/projects/${project.id}/tables/${table.id}/webhooks/${webhookToDelete.id}`,
        { method: 'DELETE' }
      );
      setWebhooks((prev) => prev.filter((h) => h.id !== webhookToDelete.id));
      showToast({ type: 'success', title: 'Webhook Deleted', message: `Webhook '${webhookToDelete.name}' removed.` });
      setWebhookToDelete(null);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Delete Failed',
        message: err?.message || 'Server error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleActive = async (hook: TableWebhook) => {
    if (!canEdit) return;
    try {
      const updated = { ...hook, is_active: !hook.is_active };
      await apiClient(
        `/projects/${project.id}/tables/${table.id}/webhooks/${hook.id}`,
        {
          method: 'PUT',
          body: JSON.stringify({ is_active: updated.is_active }),
        }
      );
      setWebhooks((prev) => prev.map((h) => (h.id === hook.id ? updated : h)));
      showToast({
        type: 'success',
        title: updated.is_active ? 'Webhook Activated' : 'Webhook Paused',
        message: `${hook.name} is now ${updated.is_active ? 'active' : 'paused'}.`,
      });
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Update Failed',
        message: err?.message || 'Failed to toggle status',
      });
    }
  };

  const handleTestWebhook = async (hook: TableWebhook) => {
    setTestingWebhookId(hook.id);
    try {
      const res = await apiClient<{
        success: boolean;
        status_code: number;
        duration_ms: number;
        error?: string;
        message?: string;
      }>(`/projects/${project.id}/tables/${table.id}/webhooks/${hook.id}/test`, {
        method: 'POST',
      });

      if (res.success) {
        setTestResults((prev) => ({
          ...prev,
          [hook.id]: {
            success: true,
            message: `HTTP ${res.status_code} OK (${res.duration_ms}ms)`,
            duration_ms: res.duration_ms,
          },
        }));
        showToast({
          type: 'success',
          title: 'Webhook Ping Successful',
          message: `Received HTTP ${res.status_code} in ${res.duration_ms}ms.`,
        });
      } else {
        const msg = res.error || `HTTP ${res.status_code}`;
        setTestResults((prev) => ({
          ...prev,
          [hook.id]: {
            success: false,
            message: msg,
            duration_ms: res.duration_ms,
          },
        }));
        showToast({
          type: 'error',
          title: 'Webhook Ping Failed',
          message: msg,
        });
      }
    } catch (err: any) {
      setTestResults((prev) => ({
        ...prev,
        [hook.id]: {
          success: false,
          message: err?.message || 'Connection failed',
        },
      }));
      showToast({
        type: 'error',
        title: 'Webhook Ping Error',
        message: err?.message || 'Network error',
      });
    } finally {
      setTestingWebhookId(null);
    }
  };

  const copyToClipboard = (text: string, type: 'secret' | 'url', id: string) => {
    navigator.clipboard.writeText(text);
    if (type === 'secret') {
      setCopiedSecrets((prev) => ({ ...prev, [id]: true }));
      setTimeout(() => setCopiedSecrets((prev) => ({ ...prev, [id]: false })), 2000);
    } else {
      setCopiedUrls((prev) => ({ ...prev, [id]: true }));
      setTimeout(() => setCopiedUrls((prev) => ({ ...prev, [id]: false })), 2000);
    }
  };

  const toggleRevealSecret = (id: string) => {
    setRevealedSecrets((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <HedgehogSpinner size="md" label="LOADING WEBHOOKS..." />
      </div>
    );
  }

  return (
    <PageContainer variant="tab">
      {/* Portaled Header Action */}
      {canEdit && headerActionContainer
        ? createPortal(
            <Button
              variant="primary"
              size="sm"
              onClick={openCreateModal}
              leftIcon={<Plus size={14} />}
            >
              Add Webhook
            </Button>,
            headerActionContainer
          )
        : null}

      {/* Webhooks Content Area */}
      {webhooks.length === 0 ? (
        <div className="p-12 text-center border border-dashed border-cf-border rounded-lg bg-cf-surface/50 space-y-3">
          <div className="w-12 h-12 rounded-full bg-cf-bg border border-cf-border flex items-center justify-center mx-auto text-cf-textSecondary">
            <Webhook size={24} />
          </div>
          <h3 className="text-sm font-semibold text-cf-textPrimary">No webhooks configured for this datastore</h3>
          <p className="text-xs text-cf-textSecondary max-w-md mx-auto">
            Connect this datastore to Zapier, Make, n8n, Slack, Discord, or your own API backend to push submission payloads immediately with HMAC verification.
          </p>
          {canEdit && (
            <Button
              variant="primary"
              size="sm"
              onClick={openCreateModal}
              leftIcon={<Plus size={13} />}
              className="mt-2"
            >
              Create First Webhook
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {webhooks.map((hook) => {
            const isRevealed = Boolean(revealedSecrets[hook.id]);
            const isCopiedSecret = Boolean(copiedSecrets[hook.id]);
            const isCopiedUrl = Boolean(copiedUrls[hook.id]);
            const testResult = testResults[hook.id];
            const isTesting = testingWebhookId === hook.id;

            return (
              <div
                key={hook.id}
                className={`p-4 rounded-lg bg-cf-surface border transition-colors flex flex-col justify-between ${
                  hook.is_active ? 'border-cf-border hover:border-cf-borderActive' : 'border-zinc-800 opacity-75'
                }`}
              >
                <div>
                  {/* Top Bar: Name & Badges */}
                  <div className="flex items-center justify-between gap-2 mb-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <Webhook size={16} className={hook.is_active ? 'text-cf-orange' : 'text-zinc-500'} />
                      <span className="font-semibold text-xs text-cf-textPrimary truncate">{hook.name}</span>
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded border uppercase ${
                          hook.event_trigger === 'all_submissions'
                            ? 'bg-zinc-800 text-zinc-300 border-zinc-700'
                            : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                        }`}
                      >
                        {hook.event_trigger === 'all_submissions' ? 'All Records' : 'Non-Spam'}
                      </span>

                      <Button
                        variant={hook.is_active ? 'outline' : 'secondary'}
                        size="xs"
                        onClick={() => toggleActive(hook)}
                        disabled={!canEdit}
                        className={`uppercase font-semibold ${
                          hook.is_active
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20'
                            : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:bg-zinc-700'
                        }`}
                        title="Click to toggle status"
                      >
                        {hook.is_active ? 'Active' : 'Paused'}
                      </Button>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="space-y-2 mt-3 text-xs text-cf-textSecondary">
                    {/* URL */}
                    <div>
                      <div className="text-xs text-cf-textMuted mb-0.5">Destination URL</div>
                      <div className="flex items-center gap-1.5 bg-cf-bg px-2.5 py-1.5 rounded border border-cf-border">
                        <span className="truncate text-cf-textPrimary flex-1 text-xs">{hook.url}</span>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => copyToClipboard(hook.url, 'url', hook.id)}
                          title="Copy destination URL"
                        >
                          {isCopiedUrl ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                        </Button>
                      </div>
                    </div>

                    {/* HMAC Secret */}
                    <div>
                      <div className="text-xs text-cf-textMuted mb-0.5 flex items-center justify-between">
                        <span className="flex items-center gap-1">
                          <ShieldCheck size={11} className="text-emerald-400" />
                          <span>HMAC-SHA256 Signing Secret</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 bg-cf-bg px-2.5 py-1.5 rounded border border-cf-border">
                        <span className="truncate text-zinc-300 flex-1 text-xs">
                          {isRevealed ? hook.secret : `${hook.secret.slice(0, 8)}••••••••••••••••••••••••••••••••`}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => toggleRevealSecret(hook.id)}
                          title={isRevealed ? 'Hide secret' : 'Reveal secret'}
                        >
                          {isRevealed ? <EyeOff size={12} /> : <Eye size={12} />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => copyToClipboard(hook.secret, 'secret', hook.id)}
                          title="Copy secret"
                        >
                          {isCopiedSecret ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Live Ping Result Banner */}
                  {testResult && (
                    <div
                      className={`mt-3 p-2 rounded text-xs border flex items-center justify-between gap-2 ${
                        testResult.success
                          ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                          : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
                      }`}
                    >
                      <div className="flex items-center gap-1.5 truncate">
                        {testResult.success ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
                        <span className="truncate">{testResult.message}</span>
                      </div>
                      <span className="text-xs opacity-75">Just now</span>
                    </div>
                  )}
                </div>

                {/* Footer Controls */}
                <div className="flex items-center justify-between pt-3 mt-4 border-t border-cf-border">
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => handleTestWebhook(hook)}
                    disabled={isTesting || !canEdit}
                    isLoading={isTesting}
                    leftIcon={!isTesting ? <Play size={12} className="text-cf-orange" /> : undefined}
                  >
                    {isTesting ? 'Pinging...' : 'Test Webhook'}
                  </Button>

                  <div className="flex items-center gap-1">
                    {canEdit && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => openEditModal(hook)}
                          title="Edit Webhook"
                        >
                          <Edit2 size={13} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => setWebhookToDelete(hook)}
                          title="Delete Webhook"
                          className="hover:text-rose-400"
                        >
                          <Trash2 size={13} />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Webhook Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingWebhook ? 'Edit Datastore Webhook' : 'Add Outgoing Datastore Webhook'}
        maxWidth="md"
      >
        <form onSubmit={handleSaveWebhook} className="space-y-4">
          <Input
            label="Webhook Name"
            type="text"
            required
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="e.g. Zapier Lead Ingress, Slack Bot Forwarder"
          />

          <Input
            label="Destination URL (HTTP / HTTPS)"
            type="url"
            required
            value={formUrl}
            onChange={(e) => setFormUrl(e.target.value)}
            placeholder="https://hooks.zapier.com/hooks/catch/..."
          />

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-cf-textSecondary">
                HMAC-SHA256 Signing Secret
              </label>
              <Button
                variant="ghost"
                size="xs"
                onClick={generateHmacSecret}
                className="text-cf-orange hover:underline h-auto p-0"
              >
                Generate Secret
              </Button>
            </div>
            <Input
              type="text"
              required
              value={formSecret}
              onChange={(e) => setFormSecret(e.target.value)}
              placeholder="64-character hex string"
              rightAction={
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => {
                    navigator.clipboard.writeText(formSecret);
                    showToast({ type: 'info', title: 'Copied', message: 'HMAC secret copied to clipboard.' });
                  }}
                  title="Copy Secret"
                >
                  <Copy size={13} />
                </Button>
              }
            />
            <p className="text-xs text-cf-textMuted mt-1">
              Ericius computes an HMAC-SHA256 header (<code className="text-zinc-300">X-Ericius-Signature: t=...,v1=...</code>) on every outgoing dispatch using this secret.
            </p>
          </div>

          <Select
            label="Trigger Condition"
            value={formEventTrigger}
            onChange={(e) => setFormEventTrigger(e.target.value)}
            options={[
              { value: 'non_spam_only', label: 'Non-Spam Submissions Only (Recommended)' },
              { value: 'all_submissions', label: 'All Submissions (Including Quarantined Spam)' },
            ]}
          />

          <div className="pt-1">
            <Checkbox
              id="webhookIsActive"
              checked={formIsActive}
              onChange={(e) => setFormIsActive(e.target.checked)}
              label="Enable this webhook for immediate dispatches upon submission"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-cf-border">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              isLoading={isSaving}
            >
              {isSaving ? 'Saving...' : editingWebhook ? 'Update Webhook' : 'Create Webhook'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={Boolean(webhookToDelete)}
        onClose={() => setWebhookToDelete(null)}
        onConfirm={handleDeleteWebhook}
        title="Delete Datastore Webhook"
        message={
          <>
            Are you sure you want to delete <strong className="text-cf-textPrimary font-semibold">{webhookToDelete?.name}</strong>? Outgoing HTTP POST dispatches to <code className="text-cf-orange">{webhookToDelete?.url}</code> will cease immediately.
          </>
        }
        confirmLabel="Delete Webhook"
        isLoading={isDeleting}
      />
    </PageContainer>
  );
};
